import { Big } from 'big.js';
import {
  type OrderType,
  OrderTypeChange,
  OrderTypeCloseLong,
  OrderTypeCloseShort,
  OrderTypeOpenLong,
  OrderTypeOpenShort,
} from '@/api/spec';
import { Side } from '@/common/constants';
import type { MarketData } from '@/common/MarketData';
import type { SizeUnit } from '@/feature/order_book/header/size_unit_toggle';
import { bigToString, divideBig, multiplyBig, stringToBig } from '@/utils/bigUtils';

const longTypes = new Set([OrderTypeOpenLong, OrderTypeCloseLong]);
const closeTypes = new Set([OrderTypeCloseLong, OrderTypeCloseShort]);

// Order types that represent a row in trade-oriented Order History: the four
// directional trades plus order modifications (Change). Forwarder operations
// such as Cancel and IncreasePositionCollateral — and any unspecified/unknown
// type — are NOT trades: they carry no direction or size, so the directional
// helpers below would default them to short/open and render a phantom row
// (e.g. a forwarder-de-duplicated collateral add showing as "Open Short / 0 /
// Market — Failed"). Those operations belong in Account History, so callers
// should gate on this allowlist before deriving directional fields.
const tradeOrderTypes = new Set([
  OrderTypeOpenLong,
  OrderTypeOpenShort,
  OrderTypeCloseLong,
  OrderTypeCloseShort,
  OrderTypeChange,
]);

export function isTradeOrderType(type: OrderType): boolean {
  return tradeOrderTypes.has(type);
}

export function getSideFromOrderType(type: OrderType): Side {
  return longTypes.has(type) ? Side.long : Side.short;
}

export function getIsCloseFromOrderType(type: OrderType): boolean {
  return closeTypes.has(type);
}

export function getIsBuyFromOrderType(type: OrderType): boolean {
  // Buy = Open Long OR Close Short
  // Sell = Open Short OR Close Long
  // Logic: (Long && Open) || (!Long && Close)
  const isLong = longTypes.has(type);
  const isClose = closeTypes.has(type);
  return (isLong && !isClose) || (!isLong && isClose);
}

export function getIsBuyFromSideAndClose(side: Side, isClose: boolean): boolean {
  return (side === Side.long && !isClose) || (side === Side.short && isClose);
}

/**
 * Trade direction — the order form's Long/Short toggle, inverse of `typeMapOnApi[side][open|close]`.
 * NOT `getSideFromOrderType`, which reports the *position* side: CloseLong is `Side.long` there but
 * a sell, so it lands on `Side.short` here. Mixing them up inverts Close* orders and the engine
 * rejects the result with CloseOrderPositionMismatch.
 */
export function getFormSideFromOrderType(type: OrderType): Side {
  return getIsBuyFromOrderType(type) ? Side.long : Side.short;
}

/**
 * Market-order effective entry price: cross the spread (a long buys the ask, a short sells the
 * bid), mark price when that book side is empty. Fill prognosis prefers mid over mark on purpose —
 * don't fold it in here.
 */
export function marketEntryPrice(
  side: Side,
  marketData?: Pick<MarketData, 'bestAskPrice' | 'bestBidPrice' | 'markPrice'>
): number | undefined {
  return (side === Side.long ? marketData?.bestAskPrice : marketData?.bestBidPrice) ?? marketData?.markPrice;
}

export function calculateMaxAllowedNotional(
  collateralValue: Big,
  leverage: string | number | Big,
  feeMicros?: string | number | bigint | null
): Big {
  const leverageBig = leverage instanceof Big ? leverage : new Big(Number(leverage) || 1);
  const feeRate = new Big(feeMicros?.toString() ?? '0').div(1_000_000);
  const marginFactor = new Big(1).div(leverageBig).plus(feeRate);
  return collateralValue.div(marginFactor);
}

export function convertSizeToQuoteValue(sizeValue: string, unit: SizeUnit, effectivePriceBig: Big): Big {
  const sizeBig = stringToBig(sizeValue);
  return unit === 'quote' ? sizeBig : multiplyBig(sizeBig, effectivePriceBig);
}

export function convertQuoteValueToSize(
  quoteValue: Big,
  unit: SizeUnit,
  effectivePriceBig: Big,
  assetDecimals: number = 4
): string {
  if (unit === 'quote') {
    return bigToString(quoteValue, 2, Big.roundDown);
  }

  if (effectivePriceBig.eq(0)) return '0';

  const assetValue = divideBig(quoteValue, effectivePriceBig);
  return bigToString(assetValue, assetDecimals, Big.roundDown);
}
